import React, { useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
    FaTachometerAlt, FaPlayCircle, FaGavel, FaStar, 
    FaMoneyBill, FaUserCircle, FaTools, FaWallet, 
    FaUsers, FaBriefcase, FaEnvelope, FaSignOutAlt 
} from 'react-icons/fa';
import loadImage from '../assets/imageLoader';

const DashboardLayout = ({ navItems: customNavItems, theme = 'emerald' }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // Default Worker Nav Items
    const defaultNavItems = [
        { path: '/worker-dashboard', exact: true, icon: <FaTachometerAlt />, name: 'Overview' },
        { path: '/worker-dashboard/running-jobs', icon: <FaBriefcase />, name: 'My Jobs' },
        // { path: '/worker-dashboard/broker-team', icon: <FaUsers />, name: 'Broker Team' },
        { path: '/messages', icon: <FaEnvelope />, name: 'Messages' },
        { path: '/worker-dashboard/bids', icon: <FaGavel />, name: 'My Activities' },
        { path: '/worker-dashboard/ratings', icon: <FaStar />, name: 'My Ratings' },
        { path: '/worker-dashboard/instruments', icon: <FaTools />, name: 'My Instruments' },
        { path: '/wallet', icon: <FaWallet />, name: 'Wallet' },
        { path: '/worker-dashboard/profile', icon: <FaUserCircle />, name: 'My Profile' },
    ];

    const navItems = customNavItems || defaultNavItems;

    // Theme colors map
    const themeColors = {
        emerald: {
            activeBg: 'bg-emerald-600',
            border: 'border-emerald-100',
            text: 'text-emerald-600',
            light: 'bg-emerald-50'
        },
        blue: {
            activeBg: 'bg-blue-600',
            border: 'border-blue-100',
            text: 'text-blue-600',
            light: 'bg-blue-50'
        },
        indigo: {
            activeBg: 'bg-indigo-600',
            border: 'border-indigo-100',
            text: 'text-indigo-600',
            light: 'bg-indigo-50'
        }
    };

    const currentTheme = themeColors[theme] || themeColors.emerald;

    const handleLogout = async () => {
        if (window.confirm("Are you sure you want to logout?")) {
            await logout(navigate);
        }
    };

    const NavItem = ({ path, exact, icon, name }) => (
        <NavLink
            to={path}
            end={exact}
            className={({ isActive }) =>
                `group flex items-center space-x-3 px-4 py-3.5 rounded-2xl transition-all duration-300 ${isActive
                    ? `${currentTheme.activeBg} text-white shadow-lg shadow-emerald-200 translate-x-1`
                    : `text-slate-500 hover:${currentTheme.light} hover:${currentTheme.text} hover:translate-x-1`
                }`
            }
        >
            <span className="text-lg transition-transform group-hover:scale-110">{icon}</span>
            <span className="font-bold text-sm tracking-tight">{name}</span>
        </NavLink>
    );

    return (
        <div className="min-h-screen bg-slate-50 flex font-sans">
            {/* --- Sidebar --- (Hidden on Mobile) */}
            <aside className="hidden md:flex w-72 bg-white border-r border-slate-200 flex-shrink-0 flex-col h-screen sticky top-0 overflow-y-auto custom-scrollbar">
                
                {/* Profile Header */}
                <div className="p-8 pb-6 flex flex-col items-center text-center">
                    <div className="relative group mb-4">
                        <div className={`absolute -inset-1 bg-gradient-to-tr from-emerald-600 to-teal-400 rounded-full opacity-20 group-hover:opacity-100 blur transition duration-500`}></div>
                        <img
                            src={loadImage(user?.picture)}
                            alt="Profile"
                            className={`relative w-20 h-20 rounded-full object-cover border-4 border-white shadow-xl`}
                        />
                    </div>
                    <h2 className="text-xl font-black text-slate-800 tracking-tighter leading-tight">{user?.name}</h2>
                    <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md mt-2 border border-emerald-100">
                        {user?.role} Mode
                    </p>
                </div>

                {/* Navigation */}
                <nav className="flex-grow px-6 py-4 space-y-1.5">
                    {navItems.map(item => <NavItem key={item.name} {...item} />)}
                </nav>

                {/* Footer Actions */}
                <div className="p-6 border-t border-slate-100">
                    <button 
                        onClick={handleLogout}
                        className="w-full flex items-center space-x-3 px-4 py-3 rounded-2xl text-slate-400 hover:bg-red-50 hover:text-red-600 transition-all duration-300"
                    >
                        <FaSignOutAlt />
                        <span className="font-bold text-sm">Logout</span>
                    </button>
                </div>
            </aside>

            {/* --- Main Content Area --- */}
            <main className="flex-grow p-6 lg:p-10 xl:p-14 bg-slate-50/50">
                <Outlet />
            </main>
        </div>
    );
};

export default DashboardLayout;